import { useOutletContext } from "react-router";

import DocumentationButton from "components/DocumentationButton";
import {
  EmptystateDataFlowColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import { Spinner } from "components/Spinner";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import { isScanRunInProgress } from "shared/Scan/access/useCanTriggerScan";
import TriggerScanButton from "shared/Scan/TriggerScanButton";
import { getDocsUrl } from "utils/getDocsUrl";
import { GET_SCAN_DETAIL } from "views/Account/Scans/ScanDetail/gql";

import { ScanResourcesOutletContext } from "../../types";

type ScanResourcesEmptyStateProps = {
  hasNoResults?: boolean;
};

const ScanResourcesEmptyState = ({ hasNoResults }: ScanResourcesEmptyStateProps) => {
  const { run, scan } = useOutletContext<ScanResourcesOutletContext>();
  const isScanInProgress = isScanRunInProgress(run?.state);

  if (hasNoResults) {
    return (
      <EmptyState
        title="No results"
        caption="Don't give up! Check the spelling or try something less specific."
        icon={EmptystateSearchNoResultsColored}
        fullHeight
        announce
      />
    );
  }

  if (isScanInProgress) {
    return (
      <EmptyState
        title="Scan in progress"
        caption="Resources will show up here as soon as we discover them."
        icon={Spinner}
        fullHeight
        announce
      />
    );
  }

  let caption = "This scan hasn't run yet. Trigger it manually to discover resources.";

  if (run?.note) {
    caption = "This scan run didn't record any resources. See the note above for details.";
  } else if (run) {
    caption =
      "This scan ran but didn't discover any resources. Trigger it again to rediscover, or check the scan configuration.";
  }

  return (
    <EmptyState
      title="No resources discovered"
      caption={caption}
      icon={EmptystateDataFlowColored}
      fullHeight
      announce
    >
      <Box gap="large">
        {scan && (
          <TriggerScanButton
            scan={scan}
            refetchQueries={[{ query: GET_SCAN_DETAIL, variables: { id: scan.id } }]}
          />
        )}
        <DocumentationButton
          to={getDocsUrl("/concepts/resources#account-level-resources")}
          label="Documentation"
        />
      </Box>
    </EmptyState>
  );
};

export default ScanResourcesEmptyState;
